import React, { Fragment } from 'react';
import { generateKeyForElement } from '../../../../../helpers/elementKeyHelper';
import { GridItemProps } from '../../../../Grid';
import Link from '../../../../Link';
import CardSection from '../../../DoubleHeader/CardSection';
import {
  isCardSection,
  LinkSection as LinkSectionType,
  NavLink,
  SimplePrimaryNavItem,
  USP,
} from '../../../types';
import withOnClick from '../../../withOnClick';
import {
  fourColumnLayoutProps,
  primarySectionProps,
  secondarySectionProps,
} from './layout';
import {
  DummySecondarySection,
  ImageContainer,
  LinkWrapper,
  Section,
  SectionHeading,
  SectionLinks,
  StyledImage,
  UspBody,
  UspContainer,
  UspHeader,
} from './styles';

type LinkSectionProps = {
  sectionNumber: number;
  gridLayout: GridItemProps;
  totalNumberOfSections: number;
  isUspCardSection: boolean;
  dummyColumSectionAtEnd?: boolean;
  resetNav: () => void;
} & LinkSectionType;

type USPSectionProps = {
  gridLayout: GridItemProps;
} & USP;

const LinkSection: React.FC<LinkSectionProps> = ({
  heading,
  links,
  sectionNumber,
  gridLayout,
  totalNumberOfSections,
  isUspCardSection,
  dummyColumSectionAtEnd = false,
  resetNav,
}) => {
  return (
    <Fragment>
      <Section {...gridLayout}>
        <SectionHeading>{heading}</SectionHeading>
        <SectionLinks>
          {links.map((link: NavLink) => (
            <LinkWrapper key={generateKeyForElement(link)}>
              <Link
                removeVisitedStyle
                href={link.url}
                onClick={withOnClick(link, resetNav).onClick}>
                {link.text}
              </Link>
            </LinkWrapper>
          ))}
        </SectionLinks>
      </Section>
      {/* Insert a dummy sections so that there are always three sections in total for
      alignment purposes. */}
      {!dummyColumSectionAtEnd &&
        !isUspCardSection &&
        totalNumberOfSections < 3 &&
        sectionNumber === 0 && <DummySecondarySection />}

      {!dummyColumSectionAtEnd && totalNumberOfSections < 2 && sectionNumber === 0 && (
        <DummySecondarySection />
      )}

      {dummyColumSectionAtEnd &&
        !isUspCardSection &&
        totalNumberOfSections <= 2 &&
        sectionNumber == totalNumberOfSections - 1 && <DummySecondarySection />}
      {dummyColumSectionAtEnd &&
        !isUspCardSection &&
        totalNumberOfSections == 1 &&
        sectionNumber == totalNumberOfSections - 1 && <DummySecondarySection />}
    </Fragment>
  );
};

const UspCardSection: React.FC<USPSectionProps> = ({
  gridLayout,
  image,
  header,
  body,
}) => {
  return (
    <Fragment>
      <Section {...gridLayout}>
        <UspContainer data-cy="USP">
          <UspHeader>
            {image && (
              <ImageContainer>
                <StyledImage image={image} />
              </ImageContainer>
            )}
            <SectionHeading>{header}</SectionHeading>
          </UspHeader>
          <UspBody>{body}</UspBody>
        </UspContainer>
      </Section>
    </Fragment>
  );
};

type SimpleNavMenuProps = {
  navItem: SimplePrimaryNavItem;
  resetNav: () => void;
  useFourColumnLayout?: boolean;
  dummyColumSectionAtEnd?: boolean;
};

const SimpleNavMenu: React.FC<SimpleNavMenuProps> = ({
  navItem: { sections, usp },
  resetNav,
  useFourColumnLayout = false,
  dummyColumSectionAtEnd = false,
}) => {
  const getGridLayout = (sectionNumber: number): GridItemProps => {
    if (useFourColumnLayout) {
      return fourColumnLayoutProps;
    } else if (sectionNumber === 0) {
      return primarySectionProps;
    } else {
      return secondarySectionProps;
    }
  };

  return (
    <>
      {sections.map((navSection, i) => {
        if (isCardSection(navSection)) {
          return (
            <Section key={generateKeyForElement(navSection)} {...getGridLayout(i)}>
              <CardSection {...navSection} />
            </Section>
          );
        }
        return (
          <LinkSection
            {...navSection}
            key={generateKeyForElement(navSection)}
            sectionNumber={i}
            gridLayout={getGridLayout(i)}
            totalNumberOfSections={sections.length}
            resetNav={resetNav}
            isUspCardSection={!!usp}
            dummyColumSectionAtEnd={dummyColumSectionAtEnd}
          />
        );
      })}

      {usp && sections.length < 3 && (
        <UspCardSection gridLayout={{ desktop: 5, tabletLandscape: 4 }} {...usp} />
      )}
    </>
  );
};

export default SimpleNavMenu;
